// src/pages/HomePage.jsx
// PAM Portal Home Page — Service tiles

import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactFooter from '../components/ContactFooter';

const services = [
    {
        id: 'access',
        icon: '🔐',
        title: 'Access to Tenants',
        description: 'Request access to Paragon Tenant permissions. Supports Individual, Ruleset, and Bindle access types.',
        route: '/tenants',
        active: true,
        color: '#22c55e',
        borderColor: '#bbf7d0',
        bg: '#f0fdf4'
    },
    {
        id: 'consultation',
        icon: '💬',
        title: 'Consultation / Feature Request',
        description: 'Use this to initiate a discussion with P&S for any kind of internal process improvement or to record any new idea/change in the existing permissions/security setup/creation of new permission pages/bindles.',
        externalUrl: 'https://w.amazon.com/bin/view/Feature_Consultation_Request',
        active: true,
        color: '#0073bb',
        borderColor: '#b8d4f0',
        bg: '#f0f8ff'
    },
    {
        id: 'uat',
        icon: '🧪',
        title: 'User Acceptance Testing (UAT)',
        description: 'Use this to request testing of new permissions/updates in existing functionality. The agenda is typically pre-defined under Consultation/Feature request.',
        externalUrl: 'https://w.amazon.com/bin/view/P&S_UAT/',
        active: true,
        color: '#7c3aed',
        borderColor: '#c4b5fd',
        bg: '#f5f3ff'
    },
    {
        id: 'data-request',
        icon: '📊',
        title: 'Data Request',
        description: 'Request data exports, reports, or access to permission-related data for analysis and compliance.',
        externalUrl: 'https://w.amazon.com/bin/view/P&S_Data_Requests/',
        active: true,
        color: '#f59e0b',
        borderColor: '#fde68a',
        bg: '#fffbeb'
    },
    {
        id: 'access-removal',
        icon: '🚫',
        title: 'Access Removal',
        description: 'Request removal of existing permissions for users who no longer require access.',
        externalUrl: 'https://w.amazon.com/bin/view/P%26S_Access_removal/',
        active: true,
        color: '#dc2626',
        borderColor: '#fecaca',
        bg: '#fef2f2'
    },
    {
        id: 'troubleshooting',
        icon: '🔧',
        title: 'Troubleshooting',
        description: 'Report issues with permissions, access, or security tools. Our team will investigate and resolve.',
        externalUrl: 'https://w.amazon.com/bin/view/P&S_Troubleshoot_SIM/',
        active: true,
        color: '#9333ea',
        borderColor: '#c4b5fd',
        bg: '#f5f3ff'
    }
];

const HomePage = ({ userAlias }) => {
    const navigate = useNavigate();

    return (
        <div style={{ minHeight: '100vh', background: '#fff' }}>
            {/* Top bar */}
            <div style={{ padding: '14px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e9ecef', background: '#fafafa' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>⚡</span>
                    <span style={{ fontWeight: '700', fontSize: '15px', color: '#333' }}>Permissions & Access Management</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <a href="/my-requests" style={{ fontSize: '14px', color: '#0073bb', textDecoration: 'none', fontWeight: '500' }}>📊 My Requests</a>
                    {userAlias && (
                        <span style={{ fontSize: '13px', color: '#555', padding: '4px 12px', borderRadius: '16px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            Welcome, <strong>{userAlias}</strong>
                        </span>
                    )}
                </div>
            </div>

            {/* Hero */}
            <div style={{ padding: '50px 40px 36px', textAlign: 'center', background: 'linear-gradient(180deg, #1a1a2e 0%, #232946 100%)', color: '#fff' }}>
                <h1 style={{ fontSize: '30px', fontWeight: '800', margin: '0 0 10px', letterSpacing: '-0.5px' }}>
                    Permissions & Access Management Portal
                </h1>
                <p style={{ fontSize: '15px', color: '#b8c1ec', margin: 0 }}>
                    Centralized access provisioning for SPS Paragon Tenants
                </p>
            </div>

            {/* Service Tiles */}
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 40px' }}>
                {/* Back to Access Genie */}
                <a href="https://accessgenie.tsi.amazon.dev/" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', background: '#232946', color: '#fff', fontSize: '14px', fontWeight: '600', textDecoration: 'none', marginBottom: '28px' }}>
                    ◀ Back to Access Genie
                </a>

                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '8px', textAlign: 'center' }}>
                    How can we help?
                </h2>
                <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '32px' }}>
                    Select a service below
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                    {services.map(service => (
                        <div
                            key={service.id}
                            onClick={() => {
                                if (service.externalUrl) {
                                    window.open(service.externalUrl, '_blank', 'noopener,noreferrer');
                                } else {
                                    navigate(service.route);
                                }
                            }}
                            style={{
                                padding: '24px 20px',
                                borderRadius: '12px',
                                border: `2px solid ${service.borderColor}`,
                                background: service.bg,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 6px 20px ${service.borderColor}`; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                        >
                            <div style={{ fontSize: '28px' }}>{service.icon}</div>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111', margin: 0 }}>{service.title}</h3>
                            <p style={{ fontSize: '13px', color: '#555', margin: 0, lineHeight: '1.5', flex: 1 }}>{service.description}</p>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: service.color, marginTop: '4px' }}>
                                Get Started →
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <ContactFooter />
        </div>
    );
};

export default HomePage;
